/**
 * Flow Serialization Service
 * Handles serialization and deserialization of ReactFlow state
 */

import type { Node, Edge, Viewport } from '@xyflow/react';
import type { FlowState, SavedFlow, FlowTemplate, FlowMetadata, FlowFileReference } from '../types/flow';
import logger from '../utils/logger';

class FlowSerializationService {
  /**
   * Serialize ReactFlow state to a SavedFlow object
   */
  serializeFlow(
    nodes: Node[],
    edges: Edge[],
    viewport: Viewport,
    metadata: Partial<FlowMetadata>,
    isTemplate: boolean = false
  ): SavedFlow {
    const now = new Date().toISOString();
    
    // Generate ID if not provided
    const id = metadata.id || this.generateId();
    
    // Extract file references from ImagePanel nodes
    const fileReferences = this.extractFileReferences(nodes);
    
    // Create complete metadata
    const fullMetadata: FlowMetadata = {
      id,
      name: metadata.name || 'Untitled Knod',
      description: metadata.description,
      createdAt: metadata.createdAt || now,
      updatedAt: now,
      version: metadata.version || '1.0.0',
      appVersion: process.env.REACT_APP_VERSION || '1.0.0',
      owner: metadata.owner,
      organization: metadata.organization,
      permission: metadata.permission || 'private',
      tags: metadata.tags || [],
      fileReferences,
      brandVoiceId: metadata.brandVoiceId,
      thumbnail: metadata.thumbnail,
      archived: metadata.archived,
      projectId: metadata.projectId
    };
    
    // Create flow state
    const state: FlowState = {
      nodes: this.sanitizeNodes(nodes),
      edges: this.sanitizeEdges(edges),
      viewport: { ...viewport }
    };
    
    return {
      metadata: fullMetadata,
      state,
      isTemplate
    };
  }
  
  /**
   * Serialize as a template
   */
  serializeTemplate(
    nodes: Node[],
    edges: Edge[],
    viewport: Viewport,
    metadata: Partial<FlowMetadata>,
    category?: string
  ): FlowTemplate {
    const flow = this.serializeFlow(nodes, edges, viewport, metadata, true);
    
    return {
      ...flow,
      isTemplate: true,
      metadata: {
        ...flow.metadata,
      },
      category,
      usageCount: 0
    } as FlowTemplate;
  }
  
  /**
   * Deserialize a SavedFlow back to ReactFlow state
   */
  deserializeFlow(savedFlow: SavedFlow): FlowState {
    return {
      nodes: this.restoreNodes(savedFlow.state.nodes),
      edges: this.restoreEdges(savedFlow.state.edges),
      viewport: savedFlow.state.viewport
    };
  }
  
  /**
   * Create a new flow instance from a template
   */
  instantiateTemplate(template: FlowTemplate, newName?: string): SavedFlow {
    const now = new Date().toISOString();
    const id = this.generateId();
    
    // Regenerate node IDs and get the mapping of old -> new IDs
    const { nodes: newNodes, idMap } = this.regenerateNodeIdsWithMap(template.state.nodes);
    
    // Update edges to use the new node IDs
    const newEdges = this.updateEdgeReferences(template.state.edges, idMap);
    
    return {
      metadata: {
        ...template.metadata,
        id,
        name: newName || `${template.metadata.name} (Copy)`,
        createdAt: now,
        updatedAt: now,
        version: '1.0.0'
      },
      state: {
        ...template.state,
        nodes: newNodes,
        edges: newEdges
      },
      isTemplate: false
    };
  }
  
  /**
   * Sanitize nodes for serialization (remove non-serializable data)
   */
  private sanitizeNodes(nodes: Node[]): Node[] {
    return nodes.map(node => ({
      ...node,
      // Remove any functions or non-serializable data from node.data
      data: this.sanitizeNodeData(node.data)
    }));
  }
  
  /**
   * Extract file references from ImagePanel nodes
   */
  private extractFileReferences(nodes: Node[]): FlowFileReference[] {
    const references: FlowFileReference[] = [];
    
    for (const node of nodes) {
      if (node.type === 'imagePanel' && node.data) {
        const data = node.data as Record<string, unknown>;
        const fileId = data.fileId as string | undefined;
        const fileName = data.fileName as string | undefined;
        const fileType = data.fileType as string | undefined;
        
        // Only add reference if we have a fileId
        if (fileId) {
          references.push({
            fileId,
            nodeId: node.id,
            fileName,
            fileType
          });
        }
      }
    }
    
    logger.debug('Extracted file references:', references);
    return references;
  }
  
  /**
   * Sanitize node data (remove callbacks, functions, and ephemeral state)
   */
  private sanitizeNodeData(data: any): any {
    if (!data) return data;
    
    const sanitized: any = {};
    
    // Keys that represent ephemeral UI state and should not be persisted
    const ephemeralKeys = ['isProcessing', 'isLoading', 'isGenerating'];
    
    for (const key in data) {
      const value = data[key];
      
      // Skip functions
      if (typeof value === 'function') {
        continue;
      }
      
      // Skip ephemeral state keys
      if (ephemeralKeys.includes(key)) {
        continue;
      }
      
      // Handle nested objects
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = this.sanitizeNodeData(value);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }
  
  /**
   * Sanitize edges for serialization
   */
  private sanitizeEdges(edges: Edge[]): Edge[] {
    return edges.map(edge => ({
      ...edge,
      // Remove any non-serializable data
      data: edge.data ? { ...edge.data } : undefined
    }));
  }
  
  /**
   * Restore nodes after deserialization
   * Note: Callbacks (onOutput, onReceiveInput) will need to be re-attached by the app
   */
  private restoreNodes(nodes: Node[]): Node[] {
    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        // Reset ephemeral state - processing state is not persisted and
        // should always start as false. Job resumption will set it true if needed.
        isProcessing: false,
      }
      // The app will need to re-attach callbacks after loading
    }));
  }
  
  /**
   * Restore edges after deserialization
   */
  private restoreEdges(edges: Edge[]): Edge[] {
    return edges.map(edge => ({
      ...edge
    }));
  }
  
  /**
   * Regenerate node IDs and return the mapping (for updating edge references)
   */
  private regenerateNodeIdsWithMap(nodes: Node[]): { nodes: Node[]; idMap: Map<string, string> } {
    const idMap = new Map<string, string>();
    
    // Generate new IDs for all nodes
    nodes.forEach(node => {
      idMap.set(node.id, this.generateNodeId());
    });
    
    // Update nodes with new IDs
    const newNodes = nodes.map(node => ({
      ...node,
      id: idMap.get(node.id) || node.id
    }));
    
    return { nodes: newNodes, idMap };
  }
  
  /**
   * Update edge source/target references based on the node ID mapping
   */
  private updateEdgeReferences(edges: Edge[], idMap: Map<string, string>): Edge[] {
    return edges.map(edge => ({
      ...edge,
      id: this.generateEdgeId(),
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target
    }));
  }
  
  /**
   * Generate a unique edge ID
   */
  private generateEdgeId(): string {
    return `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Regenerate node IDs to avoid conflicts when instantiating templates
   * @deprecated Use regenerateNodeIdsWithMap instead
   */
  // @ts-expect-error TS6133 - Deprecated but kept for reference
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _regenerateNodeIds(nodes: Node[]): Node[] {
    const idMap = new Map<string, string>();
    
    // Generate new IDs for all nodes
    nodes.forEach(node => {
      idMap.set(node.id, this.generateNodeId());
    });
    
    // Update nodes with new IDs
    return nodes.map(node => ({
      ...node,
      id: idMap.get(node.id) || node.id
    }));
  }
  
  /**
   * Generate a unique ID for flows/templates (UUID format for database compatibility)
   */
  private generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  /**
   * Generate a unique node ID
   */
  private generateNodeId(): string {
    return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Validate a saved flow structure
   */
  validateFlow(flow: SavedFlow): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!flow.metadata) {
      errors.push('Missing metadata');
    } else {
      if (!flow.metadata.id) errors.push('Missing flow ID');
      if (!flow.metadata.name) errors.push('Missing flow name');
      if (!flow.metadata.createdAt) errors.push('Missing creation date');
      if (!flow.metadata.updatedAt) errors.push('Missing update date');
    }
    
    if (!flow.state) {
      errors.push('Missing flow state');
    } else {
      if (!Array.isArray(flow.state.nodes)) errors.push('Invalid nodes array');
      if (!Array.isArray(flow.state.edges)) errors.push('Invalid edges array');
      if (!flow.state.viewport) errors.push('Missing viewport');
    }
    
    if (typeof flow.isTemplate !== 'boolean') {
      errors.push('Missing isTemplate flag');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Export flow to JSON string
   */
  exportToJSON(flow: SavedFlow): string {
    try {
      return JSON.stringify(flow, null, 2);
    } catch (error) {
      logger.error('Failed to export flow to JSON:', error);
      throw new Error('Failed to export flow');
    }
  }
  
  /**
   * Import flow from JSON string
   */
  importFromJSON(json: string): SavedFlow {
    try {
      const flow = JSON.parse(json) as SavedFlow;
      const validation = this.validateFlow(flow);
      
      if (!validation.valid) {
        throw new Error(`Invalid flow format: ${validation.errors.join(', ')}`);
      }
      
      return flow;
    } catch (error) {
      logger.error('Failed to import flow from JSON:', error);
      throw new Error('Failed to import flow');
    }
  }
}

const flowSerializationService = new FlowSerializationService();
export default flowSerializationService;
